import React, { useState, useEffect } from "react";
import {
  ExternalLink,
  ArrowRight,
  Check,
  Car,
  Loader,
  AlertCircle,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import API_CONFIG from "../../lib/ApiConfig";
import analyticsService from "../../lib/AnalyticsService";

const RefinancingHandoff = ({ vehicleData, onComplete }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [error, setError] = useState(null);
  const [submissionComplete, setSubmissionComplete] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState(null);
  const [rewardCount, setRewardCount] = useState(0);
  const [showLearnMore, setShowLearnMore] = useState(false);

  const { getIdToken } = useAuth();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.querySelector("main")?.scrollTo(0, 0);
    analyticsService.trackPageView("refi_handoff", {
      screen: "handoff",
      flow_type: "refinancing",
      has_vehicle_data: !!vehicleData,
    });
  }, [vehicleData]);

  const handleSubmitToServer = async () => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      setError(null);

      const token = await getIdToken();
      if (!token) throw new Error("Authentication required");

      const payload = {
        vin: vehicleData.vin || null,
        year: vehicleData.year || null,
        make: vehicleData.make || null,
        model: vehicleData.model || null,
        trim: vehicleData.trim || null,
        nadaUid: vehicleData.nadaUid || null,
        mileage: vehicleData.mileage || null,
        state: vehicleData.state || null,
        license: vehicleData.license || vehicleData.plate || null,
      };

      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/refinancing/submit`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-user-token": token,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Server error: ${response.status}`
        );
      }

      const responseData = await response.json();

      setRedirectUrl(responseData.redirectUrl);
      setRewardCount(responseData.rewardCount || 0);
      setSubmissionComplete(true);

      sessionStorage.setItem(
        "refinancingSubmission",
        JSON.stringify({
          flowId: responseData.flowId,
          vehicleCount: responseData.vehicleCount,
          rewardCount: responseData.rewardCount,
          submittedAt: new Date().toISOString(),
          redirectUrl: responseData.redirectUrl,
        })
      );

      analyticsService.trackEvent("Refinancing Submission Complete", {
        flow_type: "refinancing",
        flow_id: responseData.flowId,
        vehicle_count: responseData.vehicleCount,
        reward_count: responseData.rewardCount,
        has_redirect_url: !!responseData.redirectUrl,
      });

      return responseData;
    } catch (err) {
      console.error("Error submitting refinancing data:", err);
      analyticsService.trackError("refi_submission_error", err, {
        screen: "handoff",
      });
      setError(
        err.message || "Failed to submit vehicle data. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRedirect = async () => {
    if (isRedirecting) return;

    if (!submissionComplete) {
      try {
        await handleSubmitToServer();
      } catch (error) {
        return;
      }
    }

    const finalRedirectUrl =
      redirectUrl ||
      (() => {
        try {
          return JSON.parse(
            sessionStorage.getItem("refinancingSubmission")
          )?.redirectUrl;
        } catch {
          return null;
        }
      })();

    if (!finalRedirectUrl) {
      console.error("No redirect URL available");
      return;
    }

    setIsRedirecting(true);

    try {
      analyticsService.trackEvent("Refinancing Partner Redirect", {
        redirect_url_domain: new URL(finalRedirectUrl).hostname,
        reward_count: rewardCount,
      });
    } catch (e) {
      // URL parsing might fail
    }

    sessionStorage.setItem(
      "refinancingHandoff",
      JSON.stringify({
        vehicleData,
        handoffAt: new Date().toISOString(),
        partnerUrl: finalRedirectUrl,
        rewardCount,
      })
    );

    window.location.href = finalRedirectUrl;
  };

  const formatVehicleInfo = () => {
    const parts = [];
    if (vehicleData.year) parts.push(vehicleData.year);
    if (vehicleData.make) parts.push(vehicleData.make);
    if (vehicleData.model) parts.push(vehicleData.model);
    if (vehicleData.trim) parts.push(`(${vehicleData.trim})`);
    if (parts.length > 0) return parts.join(" ");
    if (vehicleData.vin) return `VIN: ${vehicleData.vin}`;
    const plate = vehicleData.plate || vehicleData.license;
    if (plate) return `Plate: ${plate}${vehicleData.state ? ` (${vehicleData.state})` : ""}`;
    return "Your vehicle";
  };

  // Loading state
  if (isSubmitting) {
    return (
      <div className="space-y-6 p-4">
        <div className="text-center space-y-4">
          <Loader className="w-12 h-12 text-[#7B61FF] animate-spin mx-auto" />
          <h1 className="text-2xl font-bold font-bebas uppercase">
            Processing Your Information
          </h1>
          <p className="text-gray-600 text-sm">
            Saving your vehicle information and preparing your refinancing
            quotes...
          </p>
        </div>

        <div className="bg-[#D4F6C9] rounded-2xl p-4">
          <div className="flex items-start gap-3">
            <Car className="w-5 h-5 text-green-700 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-bold text-darkPurple text-sm">
                {formatVehicleInfo()}
              </p>
              <p className="text-sm text-gray-600">
                {vehicleData.mileage &&
                  `${Number(vehicleData.mileage).toLocaleString()} miles`}
                {vehicleData.state && ` \u2022 ${vehicleData.state}`}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6 p-4">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold font-bebas uppercase">
            Something Went Wrong
          </h1>
          <p className="text-gray-600 text-sm">
            We encountered an issue processing your information.
          </p>
        </div>

        <div className="flex items-start gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p>{error}</p>
        </div>

        <button
          onClick={handleSubmitToServer}
          className="w-full flex items-center justify-center gap-2 bg-[#7B61FF] text-white px-4 py-4 rounded-xl hover:bg-[#6A50EE] transition-colors font-bold text-lg shadow-lg shadow-[#7B61FF]/30"
        >
          Try Again
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // Main view — connecting with Caribou
  return (
    <div className="space-y-6 p-4">
      {/* Header with Caribou logo */}
      <div className="text-center space-y-3">
        <div className="w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center mx-auto p-3">
          <img
            className="w-full h-full object-contain"
            src="https://caribou.com/wp-content/uploads/2025/10/logo-normal.svg"
            alt="Caribou"
          />
        </div>
        <h1 className="text-2xl font-bold font-bebas uppercase">
          We're connecting you with Caribou
        </h1>
        <p className="text-gray-600 text-sm">
          Get personalized auto refinancing rates for your vehicle.
        </p>
      </div>

      {/* Provider card */}
      <div className="border border-gray-200 rounded-2xl overflow-hidden">
        <div className="flex items-center gap-4 p-4">
          <div className="w-14 h-14 bg-gray-800 rounded-xl flex items-center justify-center p-2 flex-shrink-0">
            <img
              className="w-full h-full object-contain"
              src="https://caribou.com/wp-content/uploads/2025/10/logo-normal.svg"
              alt="Caribou"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold text-darkPurple">Caribou</h2>
            <p className="text-sm text-gray-500">
              Auto loan refinancing platform
            </p>
          </div>
          <button
            onClick={() => setShowLearnMore(!showLearnMore)}
            className="text-[#7B61FF] font-medium cursor-pointer text-sm flex-shrink-0"
          >
            Learn more
          </button>
        </div>

        {/* Expandable "Learn more" section */}
        <div
          className={`overflow-hidden transition-all duration-200 ${
            showLearnMore ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div className="border-t border-gray-200 p-4 space-y-3">
            <p className="text-sm font-semibold text-darkPurple">
              Caribou: Simplifying Vehicle Refinancing
            </p>
            <p className="text-sm text-gray-700">
              Caribou makes refinancing your auto loan fast, easy, and
              transparent.
            </p>
            <p className="text-sm font-semibold text-darkPurple">
              Why Choose Caribou?
            </p>
            <ul className="text-sm text-gray-700 space-y-2 list-disc pl-5">
              <li>Zero impact on your credit score to check rates</li>
              <li>100% online process with no paperwork</li>
              <li>Potential savings of hundreds per year on your auto loan</li>
              <li>Flexible terms and competitive rates</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Vehicle summary — green card */}
      <div className="bg-[#D4F6C9] rounded-2xl p-4 space-y-1">
        <p className="text-xs uppercase tracking-wide font-bold text-gray-600 mb-2">
          Your Vehicle
        </p>
        <div className="flex items-center gap-2">
          <Check className="w-4 h-4 text-green-700 flex-shrink-0" />
          <p className="text-sm font-bold text-darkPurple">
            {formatVehicleInfo()}
          </p>
        </div>
        {(vehicleData.mileage || vehicleData.state || vehicleData.vin || vehicleData.plate || vehicleData.license) && (
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-green-700 flex-shrink-0" />
            <p className="text-sm text-gray-600">
              {[
                vehicleData.mileage &&
                  `${Number(vehicleData.mileage).toLocaleString()} miles`,
                vehicleData.state,
                vehicleData.vin && `VIN: ${vehicleData.vin}`,
                (vehicleData.plate || vehicleData.license) && `Plate: ${vehicleData.plate || vehicleData.license}`,
              ]
                .filter(Boolean)
                .join(" \u2022 ")}
            </p>
          </div>
        )}
      </div>

      {/* Reward earned */}
      {submissionComplete && rewardCount > 0 && (
        <div className="bg-[#D4F6C9] rounded-2xl p-5">
          <p className="text-xs uppercase tracking-wide font-bold mb-3 text-gray-600">
            Reward Earned
          </p>
          <div className="flex items-stretch">
            <div className="flex-1 pr-4">
              <p className="text-3xl font-bold font-bebas text-darkPurple">
                ${rewardCount}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Cash earned — your data, your money
              </p>
            </div>
            <div className="border-l border-gray-400 pl-4 flex-1">
              <p className="text-3xl font-bold font-bebas text-darkPurple">
                $1,000+
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Estimated potential annual savings
              </p>
            </div>
          </div>
        </div>
      )}

      {/* CTA — Get quotes */}
      <div className="border-2 border-[#7B61FF] rounded-2xl p-5 space-y-3 bg-[#F5F2FF]">
        <p className="text-[#7B61FF] font-bold text-sm uppercase tracking-wide">
          Get Your Rates
        </p>
        <p className="text-gray-500 text-sm">
          You'll be redirected to Caribou to see personalized refinancing rates
          for your vehicle.
        </p>
        <button
          onClick={handleRedirect}
          disabled={isRedirecting}
          className="w-full flex items-center justify-center gap-2 bg-[#7B61FF] text-white px-4 py-4 rounded-xl hover:bg-[#6A50EE] transition-colors font-bold text-lg shadow-lg shadow-[#7B61FF]/30"
        >
          {isRedirecting ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              <span>Redirecting...</span>
            </>
          ) : (
            <>
              <span>Get My Rates</span>
              <ExternalLink className="w-4 h-4" />
            </>
          )}
        </button>
      </div>

      {/* What Happens Next */}
      <div className="border border-gray-200 rounded-2xl p-5 space-y-4">
        <p className="text-xs uppercase tracking-wide font-bold text-gray-500">
          What Happens Next
        </p>
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-green-700 text-sm font-bold">1</span>
            </div>
            <p className="text-sm">
              <span className="font-bold">Check your rate</span> — zero impact
              on your credit score.
            </p>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-green-700 text-sm font-bold">2</span>
            </div>
            <p className="text-sm">
              <span className="font-bold">Compare offers</span> — see how much
              you could save on your auto loan.
            </p>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-green-700 text-sm font-bold">3</span>
            </div>
            <p className="text-sm">
              <span className="font-bold">Refinance online</span> — 100% digital,
              no paperwork needed.
            </p>
          </div>
        </div>
      </div>

      <p className="text-xs text-center text-gray-400">
        You'll be redirected to Caribou to complete your refinancing quote.
      </p>
    </div>
  );
};

export default RefinancingHandoff;
